import { BaseWidgetView } from '../../widget/BaseWidgetView';
import bb from 'billboard.js/dist/billboard.pkgd';
import { GetIncidentsStatusResponse, IncidentsStatus, Status } from '@nirvana/shared/dtos/types/widgets/common';
import { TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { IncidentsNavigationParams, NavigationHelper } from '@nirvana/shared/navigation';
import { IncidentsConstants } from '../constants/IncidentsConstants';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../utils/htmlElement.utils';
import { ReportingPeriod, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { Feature } from '@nirvana/shared/constants';
import { ACTION_TAKEN } from '../../edr/incidentsGrid/types';
import { exitFullscreen, isBrowserFullscreen, onExitFullscreenError } from '../../utils/fullscreen.utils';
import { GzTextWebComponent } from '../../ui/gzText';

enum DataSets {
  BlockedIncidents,
  UnderInvestigation,
  PartiallyBlocked,
}

export class IncidentsView extends BaseWidgetView {
  public shadow: any;
  protected translations: any;
  private $chart: any;
  private $style: any;
  private colors: {
    blockedIncidents: string;
    investigatingIncidents: string;
    partiallyBlockedIncidents: string;
    errorColor: string;
  };
  private navigationHandlers: Array<(event: any) => void> = [];

  getDomElements() {
    super.getDomElements();
    this.dom.$blockedIncidentsName = this.shadow.querySelector(
      '[gz-incidents="blockedIncidents"] [gz-list-element="incidentName"]'
    );
    this.dom.$investigatingIncidentsName = this.shadow.querySelector(
      '[gz-incidents="investigatingIncidents"] [gz-list-element="incidentName"]'
    );
    this.dom.$partiallyBlockedName = this.shadow.querySelector(
      '[gz-incidents="partiallyBlockedIncidents"] [gz-list-element="incidentName"]'
    );
    this.dom.$incidentsNotAvailableMessage = this.shadow.querySelector('#incidentsNotAvailableMessage') as GzTextWebComponent;
    this.dom.$incidentsNotAvailableMspMessage = this.shadow.querySelector('#incidentsNotAvailableMspMessage') as GzTextWebComponent;
    this.dom.$listContainer = this.shadow.querySelector('#incidentsStatusList');
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#incidentsStatusTitle');
    this.dom.$chartContainer = this.shadow.querySelector('#incidentsStatusChart');
    this.dom.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.setTranslatedStrings();
    this.setErrorTooltip();
    this.getChartColors();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getIncidentsStatusWidgetStrings();
  }

  setTranslatedStrings() {
    const { title, blockedIncidentsName, investigatingIncidentsName, partiallyBlockedName } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
    this.dom.$blockedIncidentsName.innerHTML = blockedIncidentsName;
    this.dom.$investigatingIncidentsName.innerHTML = investigatingIncidentsName;
    this.dom.$partiallyBlockedName.innerHTML = partiallyBlockedName;
  }

  buildSuccessView(response: BackendResponseWithMeta<GetIncidentsStatusResponse>) {
    this.resetTemplateState(this.$wrapper);

    const result = response.widgetResult;

    if (result.status === Status.MISSING_LICENSE) {
      this.dom.$incidentsNotAvailableMessage.innerHTML = this.getTextForMissingLicence();
      this.setTemplateState(this.$wrapper, TemplateState.MISSING);
      return;
    }

    if (result.status === Status.MISSING_LICENCE_MSP) {
      this.dom.$incidentsNotAvailableMspMessage.innerHTML = this.getTextForMissingLicenceMSP();
      this.setTemplateState(this.$wrapper, TemplateState.MISSING_MSP);
      return;
    }

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate(result.data);
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      const data = result.data;
      this.buildList(data);
      this.buildChart(data);
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].reportingPeriod,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    // This is a programmatic error (breaking the contract); It should never happen
    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate(data?: IncidentsStatus) {
    if (data) {
      this.buildList(data);
    }

    const listItems = this.shadow.querySelectorAll('.gz-data-item');
    listItems.forEach(item => {
      item.querySelector('[gz-list-element="incidentValue"]').innerHTML = '0';
      item.querySelector('[gz-list-element="incidentColor"]').style.backgroundColor = this.colors.errorColor;
    });
  }

  private getChartColors() {
    const blockedColor = this.dom.$style.getPropertyValue('--green-teal-chart-300').trimLeft();
    const investigatingColor = this.dom.$style.getPropertyValue('--red-chart-600').trimLeft();
    const partiallyBlockedColor = this.dom.$style.getPropertyValue('--yellow-alert-050').trimLeft();
    const chartsErrorColor = this.dom.$style.getPropertyValue('--chart-101-pressed-dividers').trimLeft();

    this.colors = {
      blockedIncidents: blockedColor,
      investigatingIncidents: investigatingColor,
      partiallyBlockedIncidents: partiallyBlockedColor,
      errorColor: chartsErrorColor,
    };
  }

  private getColorMap(latestData: IncidentsStatus) {
    if (Object.keys(latestData).length === 1) {
      return {
        [DataSets.BlockedIncidents]: this.colors.errorColor,
        [DataSets.UnderInvestigation]: this.colors.errorColor,
        [DataSets.PartiallyBlocked]: this.colors.errorColor,
      };
    }

    return {
      [DataSets.BlockedIncidents]: this.colors.blockedIncidents,
      [DataSets.UnderInvestigation]: this.colors.investigatingIncidents,
      [DataSets.PartiallyBlocked]: this.colors.partiallyBlockedIncidents,
    };
  }

  private buildList(latestData: IncidentsStatus): void {
    const dataKeys = Object.keys(latestData);
    const incidentsKeys = IncidentsConstants.LIST_ELEMENTS;

    for (const key of incidentsKeys) {
      if (!dataKeys.includes(key)) {
        this.handleListElementNoContent(key);
        continue;
      }
      this.handleListElementContent(latestData, key);
    }
  }

  private handleListElementNoContent(section: string): void {
    const $listElement = this.shadow.querySelector(`[gz-incidents="${section}"]`);
    elementClassListAdd($listElement, ElementClass.INCIDENTS_NO_LIST_CONTENT);
  }

  private handleListElementContent(latestData: IncidentsStatus, section: string): void {
    const $listElement = this.shadow.querySelector(`[gz-incidents="${section}"]`);
    $listElement.classList.remove('no-list-content');
    elementClassListRemove($listElement, ElementClass.INCIDENTS_NO_LIST_CONTENT);

    const $valueElement = this.shadow.querySelector(`[gz-incidents="${section}"] [gz-list-element="incidentValue"]`);
    const $threatColor = this.shadow.querySelector(`[gz-incidents="${section}"] [gz-list-element="incidentColor"]`);

    if (Object.keys(latestData).length === 1) {
      //We need to enforce no color or value shown if we are missing a EDR tab
      $valueElement.innerHTML = '';
      $threatColor.style.backgroundColor = 'transparent';
      return;
    }

    $valueElement.innerHTML = latestData[section];
    $threatColor.style.backgroundColor = this.colors[section];
  }

  private renderBBChartTooltip([{ value, index }]): string {
    // tooltip used for the donut chart made with billboard.js library
    const { blockedIncidentsName, investigatingIncidentsName, partiallyBlockedName } = this.translations;
    const legend = [blockedIncidentsName, investigatingIncidentsName, partiallyBlockedName];

    return `
      <div class="chart-tooltip">
        <span class="name">${legend[index]}</span>
        <span class="value">${value}</span>
      </div>
    `;
  }

  private buildChart(latestData: IncidentsStatus) {
    const colors = this.getColorMap(latestData);

    this.$chart = bb.generate({
      data: {
        columns: this.getIncidentsDataForChart(latestData),
        type: 'donut',
        colors: colors,
      },
      donut: {
        title: this.getIncidentsSum(latestData).toString(),
        label: {
          show: false,
        },
        width: 7,
        padAngle: 0.04,
      },
      expand: false,
      tooltip: {
        show: true,
        contents: this.renderBBChartTooltip.bind(this),
      },
      legend: {
        show: false,
      },
      bindto: this.dom.$chartContainer,
    });
  }

  private getIncidentsDataForChart(data: IncidentsStatus): Array<any> {
    // create columns for chart
    return [
      [DataSets.BlockedIncidents, data.blockedIncidents],
      [DataSets.UnderInvestigation, data.investigatingIncidents],
      [DataSets.PartiallyBlocked, data.partiallyBlockedIncidents],
    ].filter(column => column[1] && column[1] > 0);
  }

  private getIncidentsSum(data: IncidentsStatus): number {
    return (data.blockedIncidents || 0) + (data.investigatingIncidents || 0) + (data.partiallyBlockedIncidents || 0);
  }

  private getTextForMissingLicence(): string {
    return this.dependencies.translateFn(
      "No information available. To retrieve data about your company's {bold}Incidents Status{/bold}, please consider a compatible {link}license key{/link}.",
      {
        bold: '<strong>',
        '/bold': '</strong>',
        link: `<gz-link type="underline" class="text-link" href=${this.getIncidentStatusMissingLicenceLink(
          this.dependencies.getUserLanguage()
        )}><span>`,
        '/link': '</span></gz-link>',
        interpolation: {
          escapeValue: false,
        },
      }
    );
  }

  private getTextForMissingLicenceMSP(): string {
    return this.dependencies.translateFn(
      "No information available. To retrieve data about your company's {bold}Incidents Status{/bold}, please contact your Bitdefender Partner and upgrade the licensing plan.",
      {
        bold: '<strong>',
        '/bold': '</strong>',
        interpolation: {
          escapeValue: false,
        },
      }
    );
  }

  private getIncidentStatusMissingLicenceLink(locale: string): string {
    // We want to send links based on language, not on locale. I.e., for fr_FR and fr_US we'll use the
    // same link, for French.
    const language = locale.indexOf('_') >= 0 ? locale.split('_')[0] : locale;

    switch (language.toLowerCase()) {
      case 'en':
        return 'https://www.bitdefender.com/business/compare.html';
      case 'de':
        return 'https://www.bitdefender.de/business/compare.html';
      case 'es':
        return 'https://www.bitdefender.es/business/compare.html';
      case 'fr':
        return 'https://www.bitdefender.fr/business/compare.html';
      case 'it':
        return 'https://www.bitdefender.it/business/compare.html';
      case 'pt':
        return 'https://www.bitdefender.pt/business/compare.html';
      case 'ro':
        return 'https://www.bitdefender.ro/business/compare.html';
      default:
        // Use the english version for all other languages.
        return 'https://www.bitdefender.com/business/compare.html';
    }
  }

  private addDrillDownHandlers(targets: Array<Target>, reportingPeriod: ReportingPeriod, canDrillDown: boolean) {
    if (!NavigationHelper.shouldAddHandlers(this.dependencies.Ext)) {
      return;
    }

    if (!reportingPeriod.current.fromTimestamp || !reportingPeriod.current.toTimestamp) {
      throw new Error('Missing reporting interval margins.');
    }

    const navigationItems = this.getNavigationItems(targets, reportingPeriod);

    let navigationHandler;
    if (canDrillDown) {
      navigationHandler = (event, navigationParams) => {
        if (BaseWidgetView.isSuccessState(this.$wrapper)) {
          if (isBrowserFullscreen()) {
            exitFullscreen(() => {}, onExitFullscreenError.bind(this));
          }

          NavigationHelper.navigateToIncidents(this.dependencies.Ext, navigationParams as IncidentsNavigationParams);
        }
      };
    }

    NavigationHelper.updateNavigableItems(navigationItems, this.navigationHandlers, navigationHandler);
  }

  getNavigationItems(targets: Array<Target>, reportingPeriod: ReportingPeriod) {
    const commonFilters: any = [
      {
        lastUpdated: {
          timeRange: {
            kind: TimeRangeKind.ABSOLUTE_TIME_RANGE,
            absolute: {
              fromTimestamp: reportingPeriod.current.fromTimestamp,
              toTimestamp: reportingPeriod.current.toTimestamp,
            },
          },
        },
      },
      {
        'company.id': {
          in: targets.map(target => target.companyId),
        },
      },
    ];

    return [
      {
        elements: [this.dom.$widgetTitle],
      },
      {
        elements: [this.shadow.querySelector(`[gz-incidents="blockedIncidents"] .item-details`)],
        params: commonFilters.concat([
          {
            mainAction: {
              in: [ACTION_TAKEN.BLOCKED],
            },
          },
        ]),
      },
      {
        elements: [this.shadow.querySelector(`[gz-incidents="investigatingIncidents"] .item-details`)],
        params: commonFilters.concat([
          {
            mainAction: {
              in: [ACTION_TAKEN.REPORTED],
            },
          },
        ]),
      },
      {
        elements: [this.shadow.querySelector(`[gz-incidents="partiallyBlockedIncidents"] .item-details`)],
        params: commonFilters.concat([
          {
            mainAction: {
              in: [ACTION_TAKEN.PARTIALLY_BLOCKED],
            },
          },
        ]),
      },
    ];
  }
}
